import { apiClient } from '@/shared/api/client';
import type { LegalSlug } from '@/shared/routes/paths';

// ── Slate content model ──────────────────────────────────────────────────────
//
// Mirrors b2b-api's recursive allow-list (validators/legal.validator.ts) and
// b2b-admin's editor types. The renderer in Legal/renderSlate.tsx handles
// exactly these node types; the API guarantees nothing else is ever stored.

export type SlateBlockType =
  | 'paragraph'
  | 'heading-two'
  | 'heading-three'
  | 'bulleted-list'
  | 'numbered-list'
  | 'list-item';

export interface SlateText {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

export interface SlateBlock {
  type: SlateBlockType;
  children: SlateNode[];
}

export interface SlateLink {
  type: 'link';
  url: string;
  children: SlateNode[];
}

export type SlateElement = SlateBlock | SlateLink;
export type SlateNode = SlateText | SlateElement;

export interface LegalDocument {
  slug: LegalSlug;
  title: string;
  content: SlateNode[];
  publishedAt: string;
}

export interface CookieBannerCategory {
  title: string;
  description: string;
}

export interface CookieBannerConfig {
  enabled: boolean;
  title: string;
  body: string;
  buttons: {
    acceptAll: string;
    rejectAll: string;
    manage: string;
    savePreferences: string;
  };
  categories: {
    necessary: CookieBannerCategory;
    analytics: CookieBannerCategory;
    sale: CookieBannerCategory;
  };
  privacyPolicyLinkText: string;
  gpcNoticeText: string;
  footerLinkText: string;
}

export interface CookieBannerResponse {
  enabled: boolean;
  version: number;
  config: CookieBannerConfig | null;
  /**
   * Server-side kill switch for the HubSpot tracking pixel.
   *
   * Rides along on this endpoint because it is the one config call every visitor
   * already makes on every page, public or authenticated — but it is hoisted out
   * of `config` alongside `enabled`/`version` on purpose. Values inside `config`
   * are versioned banner copy, and republishing them re-prompts every visitor
   * for consent; an operational toggle must not have that side effect.
   */
  hubspotPixelEnabled: boolean;
}

// b2b-api's `{ data, meta }` envelope; the newsletter endpoint uses the same.
interface Envelope<T> {
  data: T;
  meta?: { correlationId: string; timestamp: string };
}

/** Published legal document. Rejects (404) when the slug was never published. */
export const fetchLegalDocument = async (
  slug: LegalSlug
): Promise<LegalDocument> => {
  const res = await apiClient.get<Envelope<LegalDocument>>(
    `/api/v1/legal/documents/${slug}`
  );
  return res.data;
};

/**
 * Published cookie banner config. Never 404s — `{ enabled: false }` is the
 * legitimate "no banner configured" answer, so this is safe to call on first
 * paint of every public page.
 */
export const fetchCookieBanner = async (): Promise<CookieBannerResponse> => {
  const res = await apiClient.get<Envelope<CookieBannerResponse>>(
    '/api/v1/legal/cookie-banner'
  );
  return res.data;
};
